import type { PlayerProgress, MistakeEntry, Subject } from './types';

const MAX_MISTAKES = 100;
const DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Derive a stable questionId from a question object.
 * Uses `id` if present (future-proofing), otherwise hashes the prompt string
 * with a simple djb2 hash — good enough for deduplication.
 */
export function deriveQuestionId(prompt: string, explicitId?: string): string {
  if (explicitId) return explicitId;
  // djb2 hash
  let hash = 5381;
  for (let i = 0; i < prompt.length; i++) {
    hash = ((hash << 5) + hash) + prompt.charCodeAt(i);
    hash |= 0; // keep 32-bit
  }
  return `q${(hash >>> 0).toString(16)}`;
}

/**
 * Record a wrong answer into the progress.mistakes array.
 *
 * Deduplication: if the same questionId was recorded in the last 24 h,
 * only update the timestamp (don't add a second entry).
 *
 * FIFO cap: after adding, if the array exceeds MAX_MISTAKES, remove the oldest entries.
 */
export function recordMistake(
  progress: PlayerProgress,
  opts: {
    questionId: string;
    kp: string;
    subject: Subject;
    prompt: string;
    correctAnswer: string | number;
    userAnswer: string | number | null;
    /** 「為什麼錯 / 怎麼算」說明,讓錯題本能教。Optional,缺省不存。 */
    explanation?: string;
  },
): void {
  const now = Date.now();
  const existing = progress.mistakes.findIndex(
    (m) => m.questionId === opts.questionId && now - m.timestamp < DEDUPE_WINDOW_MS,
  );

  if (existing !== -1) {
    // Bump timestamp so it stays fresh; preserve errorType and retried state
    progress.mistakes[existing].timestamp = now;
    progress.mistakes[existing].userAnswer = opts.userAnswer;
    progress.mistakes[existing].retried = false; // reset retry flag on new wrong attempt
    // Backfill 說明 if this attempt carries one and the stored row lacks it
    // (e.g. an older re-photographed entry now arrives with an OCR explanation).
    if (opts.explanation && !progress.mistakes[existing].explanation) {
      progress.mistakes[existing].explanation = opts.explanation;
    }
    return;
  }

  const entry: MistakeEntry = {
    questionId: opts.questionId,
    kp: opts.kp,
    subject: opts.subject,
    prompt: opts.prompt,
    correctAnswer: opts.correctAnswer,
    userAnswer: opts.userAnswer,
    timestamp: now,
    retried: false,
  };
  // Only attach when present so legacy serialisation stays untouched.
  if (opts.explanation) entry.explanation = opts.explanation;

  progress.mistakes.push(entry);

  // FIFO: drop oldest entries if over the cap
  if (progress.mistakes.length > MAX_MISTAKES) {
    progress.mistakes.splice(0, progress.mistakes.length - MAX_MISTAKES);
  }
}

/**
 * Return mistakes sorted newest-first, optionally excluding retried ones.
 */
export function getMistakes(
  progress: PlayerProgress,
  opts: { excludeRetried?: boolean } = {},
): MistakeEntry[] {
  let list = progress.mistakes.slice();
  if (opts.excludeRetried) {
    list = list.filter((m) => !m.retried);
  }
  return list.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Mark a mistake entry as retried=true (called when the player answers correctly on retry).
 */
export function markRetried(progress: PlayerProgress, questionId: string): void {
  const entry = progress.mistakes.find((m) => m.questionId === questionId);
  if (entry) entry.retried = true;
}

/**
 * Tag a mistake entry with an error type (self-reflection label).
 */
export function tagErrorType(
  progress: PlayerProgress,
  questionId: string,
  errorType: MistakeEntry['errorType'],
): void {
  const entry = progress.mistakes.find((m) => m.questionId === questionId);
  if (entry) entry.errorType = errorType;
}

/**
 * Batch-enroll wrong answers from a paper-mode exam submission.
 *
 * Each item has:
 *   - question: the GeneratedQuestion that was answered wrongly
 *   - userAnswer: what the player submitted (null = skipped)
 *
 * Internally calls recordMistake() for each, which handles deduplication
 * and the FIFO cap.
 */
export function enrollMistakes(
  progress: PlayerProgress,
  wrongs: Array<{
    question: import('../learn/generators/math/types').GeneratedQuestion;
    userAnswer: string | number | null;
  }>,
  subject: Subject,
): void {
  const now = Date.now();
  for (const { question, userAnswer } of wrongs) {
    const questionId = deriveQuestionId(
      question.prompt,
      (question as unknown as { id?: string }).id,
    );
    // v1.1 worked-example: schema-build 題不會錯誤記錄 (commitAnswer
     // 不呼叫 recordMistake);若意外流入,fallback 空字串保 schema 完整.
    const correctAns =
      question.kind === 'mcq' ? question.correctAnswer
      : question.kind === 'fill' ? question.correctAnswer
      : '';
    // mcq/fill carry an `explanation`; worked-example folds it into steps (none here).
    const explanation =
      question.kind === 'mcq' || question.kind === 'fill'
        ? question.explanation
        : undefined;
    recordMistake(progress, {
      questionId,
      kp: question.kp,
      subject,
      prompt: question.prompt,
      correctAnswer: correctAns,
      userAnswer,
      explanation,
    });
    // Set nextReviewDate to tomorrow (spaced repetition day-1 interval)
    const entry = progress.mistakes.find((m) => m.questionId === questionId);
    if (entry) {
      const tomorrow = new Date(now + 24 * 60 * 60 * 1000);
      (entry as MistakeEntry & { nextReviewDate?: string }).nextReviewDate =
        tomorrow.toISOString().slice(0, 10);
    }
  }
}
